import type { ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/lib';
import { richTextToolbarStyles } from '@/shared/ui/rich-text-editor/styles';

/** Icon size for every toolbar control, kept in one place. */
export const toolbarIconClass = 'h-4 w-4';

export type ToolbarButtonProps = {
  /** Accessible name. An icon-only control must carry one. */
  label: string;
  onClick: () => void;
  /** Toggle state. Omit for a plain action — the attribute is then absent. */
  pressed?: boolean;
  disabled?: boolean;
  /** `danger` is reserved for the destructive control (delete table). */
  variant?: 'ghost' | 'danger';
  children: ReactNode;
};

/**
 * A single toolbar control.
 *
 * The icon is decorative (`aria-hidden`); the accessible name lives on the
 * button, because the button is the thing being operated. Toggles expose their
 * state through `aria-pressed` rather than through colour alone.
 */
export function ToolbarButton({
  label,
  onClick,
  pressed,
  disabled = false,
  variant = 'ghost',
  children,
}: ToolbarButtonProps) {
  return (
    <Button
      variant={variant}
      size="xs"
      aria-label={label}
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      className={cn(pressed && richTextToolbarStyles.controlActive)}
    >
      {children}
    </Button>
  );
}

/** A semantic break between control groups. */
export function Divider() {
  return <span role="separator" aria-orientation="vertical" className={richTextToolbarStyles.divider} />;
}
